import {
  Divider,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  Provider,
  type AzureDisk,
  type InventorySnapshotVolume,
  type GcpDiskSnapshotProperties,
} from '@repo/api-gw-sdk';

import { isEmptyObject } from '@/utils/emptyUtil';
import { fileSizeFormatter } from '@/utils/fileSizeFormatter';

import type { VolumeOrDisk } from '../panels/instance/tabs/snapshotDetailsBody';
import { Icon } from '../shared/icon';
import { Tags } from '../tags';

export const VolumeInfo = ({
  volume,
  cloudProvider,
}: {
  volume: VolumeOrDisk;
  cloudProvider: Provider;
}) => {
  return (
    <>
      <Tooltip
        disableInteractive={false}
        leaveTouchDelay={2000}
        disableFocusListener
        slotProps={{
          tooltip: {
            sx: {
              maxWidth: '320px',
              width: 'fit-content',
              backgroundColor: 'transparent',
            },
          },
        }}
        title={
          cloudProvider === Provider.Aws ? (
            <AwsVolumeDetailsPopper
              volume={volume as InventorySnapshotVolume}
            />
          ) : cloudProvider === Provider.Gcp ? (
            <GcpDiskDetailsPopper disk={volume as GcpDiskSnapshotProperties} />
          ) : cloudProvider === Provider.Azure ? (
            <AzureDiskDetailsPopper disk={volume as AzureDisk} />
          ) : null
        }
      >
        <Icon
          iconClass='material-symbols-info-outline'
          className='text-gray-500'
        />
      </Tooltip>
    </>
  );
};

const PopperWrapper = ({ children }: { children: React.ReactNode }) => {
  return (
    <Stack
      padding='24px'
      borderRadius='16px'
      boxShadow='0px 10px 30px 0px rgba(0, 0, 0, 0.10)'
      gap='24px'
      sx={{
        backgroundColor: 'var(--mui-palette-background-paper)',
      }}
      divider={<Divider />}
    >
      {children}
    </Stack>
  );
};

const AwsVolumeDetailsPopper = ({
  volume: volume,
}: {
  volume: InventorySnapshotVolume;
}) => {
  return (
    <PopperWrapper>
      <Table size='small'>
        <TableBody>
          <VolDetailsRow label='Type' value={volume.volumeSettings.type} />
          {volume.volumeSettings.sizeBytes && (
            <VolDetailsRow
              label='Size'
              value={fileSizeFormatter(
                volume.volumeSettings.sizeBytes,
                1,
                false
              )}
            />
          )}
          <VolDetailsRow label='IOPS' value={volume.volumeSettings.iops} />
          <VolDetailsRow
            label='Throughput'
            value={volume.volumeSettings.throughput}
          />
        </TableBody>
      </Table>
      {!isEmptyObject(volume.tags) && (
        <Stack gap='12px'>
          <Typography variant='subtitle2'>Tags</Typography>
          <Stack>
            <Tags tags={volume.tags || {}} />
          </Stack>
        </Stack>
      )}
    </PopperWrapper>
  );
};

const GcpDiskDetailsPopper = ({
  disk,
}: {
  disk: GcpDiskSnapshotProperties;
}) => {
  return (
    <PopperWrapper>
      <Table size='small'>
        <TableBody>
          <VolDetailsRow label='Type' value={disk.diskType} />
          {disk.diskSizeBytes && (
            <VolDetailsRow
              label='Size'
              value={fileSizeFormatter(disk.diskSizeBytes, 1, false)}
            />
          )}
          <VolDetailsRow label='IOPS' value={disk.iops} />
          <VolDetailsRow label='Throughput' value={disk.throughput} />
        </TableBody>
      </Table>
      {!!disk.tags?.length && (
        <Stack gap='12px'>
          <Typography variant='subtitle2'>Labels</Typography>
          <Stack>
            <Tags tags={disk.tags || {}} />
          </Stack>
        </Stack>
      )}
    </PopperWrapper>
  );
};

const AzureDiskDetailsPopper = ({ disk }: { disk: AzureDisk }) => {
  return (
    <PopperWrapper>
      <Table size='small'>
        <TableBody>
          <VolDetailsRow label='Type' value={disk.settings.type} />
          {disk.settings.sizeBytes && (
            <VolDetailsRow
              label='Size'
              value={fileSizeFormatter(disk.settings.sizeBytes, 1, false)}
            />
          )}
          <VolDetailsRow label='Tier' value={disk.settings.tier} />
          <VolDetailsRow
            label='Hyper-V Generation'
            value={disk.settings.hyperVGeneration}
          />
        </TableBody>
      </Table>
      {!!disk.settings.tags?.length && (
        <Stack gap='12px'>
          <Typography variant='subtitle2'>Tags</Typography>
          <Tags tags={disk.settings.tags || {}} />
        </Stack>
      )}
    </PopperWrapper>
  );
};

const VolDetailsRow = ({
  label,
  value,
}: {
  label: string;
  value?: string | number;
}) => {
  return (
    value && (
      <TableRow
        sx={{
          '&:last-child td': { paddingBottom: '0px' },
          '&:first-child td': { paddingTop: '0px' },
        }}
      >
        <TableCell sx={{ border: 'none', paddingLeft: '0px' }}>
          {label}
        </TableCell>
        <TableCell sx={{ border: 'none' }}>{value}</TableCell>
      </TableRow>
    )
  );
};
